import { Text as RNText, View } from "react-native";
import {
	VictoryAxis,
	VictoryChart,
	VictoryGroup,
	VictoryLabel,
	VictoryLine,
	VictoryScatter,
	VictoryTheme,
} from "victory-native";
import { useAppTheme } from "../../../useAppTheme";
import { CHART_DIMENSIONS, getChartFontFamily } from "../constants";
import { useChartFontScale } from "../hooks/useChartFontScale";
import type { LineChartData } from "../types";

type TestReadinessLineChartProps = {
	data: LineChartData;
	appFont?: string;
	containerWidth: number;
};

const getTargetLineData = (
	targetValue: number | undefined,
	actual: Array<{ x: string | number; y: number }>,
	prediction?: Array<{ x: string | number; y: number }>,
) => {
	if (targetValue === undefined || actual.length === 0) {
		return null;
	}
	return [
		{ x: actual[0].x, y: targetValue },
		{
			x: prediction?.[prediction.length - 1]?.x ?? actual[actual.length - 1].x,
			y: targetValue,
		},
	];
};

const getSeparatorLineData = (
	prediction?: Array<{ x: string | number; y: number }>,
) => {
	if (!prediction || prediction.length === 0) {
		return null;
	}
	return [
		{ x: prediction[0].x, y: 0 },
		{ x: prediction[0].x, y: 100 },
	];
};

const LINE_CHART_ASPECT_RATIO = 0.55;

const getTargetLabelPosition = (
	targetValue: number | undefined,
	chartWidth: number,
	chartHeight: number,
	config: (typeof CHART_DIMENSIONS)["lineChart"],
) => {
	const targetLabelY =
		targetValue !== undefined
			? config.padding.top +
				(1 - targetValue / 100) *
					(chartHeight - config.padding.top - config.padding.bottom)
			: 20;
	const targetLabelX = chartWidth - 5;
	return { x: targetLabelX, y: targetLabelY };
};

export const TestReadinessLineChart = ({
	data,
	appFont,
	containerWidth,
}: TestReadinessLineChartProps) => {
	const theme = useAppTheme();
	const config = CHART_DIMENSIONS.lineChart;
	const fontScale = useChartFontScale(containerWidth);
	const { actual, prediction, targetValue, xAxisLabel, yAxisLabel } = data;

	const chartWidth = containerWidth;
	const chartHeight = Math.round(containerWidth * LINE_CHART_ASPECT_RATIO);

	const targetLineData = getTargetLineData(targetValue, actual, prediction);
	const separatorLineData = getSeparatorLineData(prediction);
	const targetLabelPos = getTargetLabelPosition(
		targetValue,
		chartWidth,
		chartHeight,
		config,
	);

	const allXValues = [
		...actual.map((d) => d.x),
		...(prediction?.map((d) => d.x) ?? []),
	];
	const uniqueXValues = [...new Set(allXValues)];

	const targetFontSize = config.axisLabelFontSize * fontScale;

	const confidenceHigh =
		theme.colors.confidence?.high ?? theme.colors.progress?.high ?? "#66bb68";
	const actualColor = theme.colors.primary;
	const predictionColor = "#4b6cb7";
	const projectionStartX =
		prediction && prediction.length > 0
			? uniqueXValues.indexOf(prediction[0].x)
			: -1;
	const plotWidth = chartWidth - config.padding.left - config.padding.right;
	const projectionStartLeft =
		projectionStartX > -1 && uniqueXValues.length > 1
			? config.padding.left +
				(projectionStartX / (uniqueXValues.length - 1)) * plotWidth
			: null;

	return (
		<View style={{ position: "relative" }}>
			<VictoryChart
				theme={VictoryTheme.material}
				domain={{ y: [0, 100] }}
				padding={config.padding}
				height={chartHeight}
				width={chartWidth}
				categories={{ x: uniqueXValues as string[] }}
			>
				<VictoryAxis
					label={xAxisLabel ?? "Time"}
					axisLabelComponent={
						<VictoryLabel
							dy={20}
							style={{
								fontSize: config.axisLabelFontSize * fontScale,
								fontFamily: getChartFontFamily(appFont),
								fill: theme.colors.onSurfaceVariant,
							}}
						/>
					}
					style={{
						axis: {
							stroke: theme.colors.outlineVariant,
							strokeWidth: 0.5,
						},
						ticks: { size: 0, stroke: "transparent" },
						tickLabels: {
							fontSize: config.tickLabelFontSize * fontScale,
							fontFamily: getChartFontFamily(appFont),
							fill: theme.colors.onSurfaceVariant,
						},
						grid: {
							stroke: theme.colors.surfaceVariant,
							strokeWidth: 0.5,
							strokeDasharray: "2,2",
						},
					}}
					tickFormat={(t) => String(t)}
				/>

				<VictoryAxis
					dependentAxis
					label={yAxisLabel ?? "Readiness"}
					axisLabelComponent={
						<VictoryLabel
							dy={-25}
							y={
								config.padding.top +
								(chartHeight - config.padding.top - config.padding.bottom) / 2
							}
							style={{
								fontSize: config.axisLabelFontSize * fontScale,
								fontFamily: getChartFontFamily(appFont),
								fill: theme.colors.onSurfaceVariant,
							}}
						/>
					}
					style={{
						axis: {
							stroke: theme.colors.outlineVariant,
							strokeWidth: 0.5,
						},
						ticks: { size: 0, stroke: "transparent" },
						tickLabels: {
							fontSize: config.tickLabelFontSize * fontScale,
							fontFamily: getChartFontFamily(appFont),
							fill: theme.colors.onSurfaceVariant,
						},
						grid: {
							stroke: ({ tick }: { tick?: number }) =>
								tick === 0 ? "transparent" : theme.colors.surfaceVariant,
							strokeWidth: 0.5,
							strokeDasharray: "2,2",
						},
					}}
					tickValues={[0, 25, 50, 75, 100]}
					tickFormat={(t) => `${t}`}
				/>

				{targetLineData && (
					<VictoryLine
						data={targetLineData}
						style={{
							data: {
								stroke: confidenceHigh,
								strokeWidth: 1,
								strokeDasharray: "3,3",
							},
						}}
					/>
				)}

				{separatorLineData && (
					<VictoryLine
						data={separatorLineData}
						style={{
							data: {
								stroke: theme.colors.outlineVariant,
								strokeWidth: 0.5,
							},
						}}
					/>
				)}

				<VictoryGroup>
					<VictoryLine
						data={actual}
						style={{
							data: {
								stroke: actualColor,
								strokeWidth: 1,
							},
						}}
					/>

					{actual.length > 1 && (
						<VictoryScatter
							data={actual.slice(0, -1).filter((_, i) => i !== 0)}
							size={3.34}
							style={{
								data: {
									fill: theme.colors.surface,
									stroke: actualColor,
									strokeWidth: 1,
								},
							}}
						/>
					)}

					{actual.length > 0 && (
						<VictoryScatter
							data={[actual[actual.length - 1]]}
							size={3.34}
							style={{
								data: {
									fill: actualColor,
									stroke: actualColor,
									strokeWidth: 1,
								},
							}}
						/>
					)}
				</VictoryGroup>

				{prediction && prediction.length > 0 && (
					<VictoryLine
						data={prediction}
						style={{
							data: {
								stroke: predictionColor,
								strokeWidth: 1,
								strokeDasharray: "6,4",
							},
						}}
					/>
				)}
			</VictoryChart>

			{targetLineData && targetValue !== undefined && (
				<RNText
					style={{
						position: "absolute",
						left: chartWidth - config.padding.right + 5,
						top: targetLabelPos.y - targetFontSize / 2,
						fontSize: targetFontSize,
						lineHeight: targetFontSize,
						fontFamily: getChartFontFamily(appFont),
						color: confidenceHigh,
						textAlign: "right",
					}}
				>
					Target
				</RNText>
			)}

			{projectionStartLeft != null && (
				<RNText
					style={{
						position: "absolute",
						left: projectionStartLeft + 6,
						top: config.padding.top + 6,
						fontSize: targetFontSize * 0.9,
						color: theme.colors.onSurfaceVariant,
						backgroundColor: theme.colors.surface,
						paddingHorizontal: 4,
						paddingVertical: 1,
						borderRadius: 6,
					}}
				>
					Projection
				</RNText>
			)}
		</View>
	);
};
